/**
 * Forage Page
 * Context-driven cross-era music discovery
 */

import { useState } from 'react';
import Masonry from 'react-masonry-css';
import { useForageStore } from '../stores/forage-store';
import { usePlaylistExportStore } from '../stores/playlist-export.store';
import { performForage } from '../services/forage.service';
import TrackCard from '../components/TrackCard';
import ExportPlaylistModal from '../components/ExportPlaylistModal';
import { wanderingModal, WanderingModal } from '../components/WanderingModal';

export default function Forage() {
  const {
    seeking,
    context,
    avoid,
    results,
    interpretation,
    loading,
    error,
    cacheHit,
    responseTimeMs,
    selectedTracks,
    setSeeking,
    setContext,
    setAvoid,
    setForageResults,
    setLoading,
    setError,
    toggleTrackSelection,
    getSelectedTracks,
  } = useForageStore();

  const { openExportModal } = usePlaylistExportStore();
  const [searchDepth, setSearchDepth] = useState<'quick' | 'balanced' | 'deep'>('balanced');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seeking.trim() || !context.trim()) return;

    wanderingModal.show();
    setLoading(true);
    setError(null);

    try {
      const response = await performForage({
        seeking: seeking.trim(),
        context: context.trim(),
        avoid: avoid.trim() || undefined,
        search_depth: searchDepth,
      });
      setForageResults(response);
    } catch (err: any) {
      const message =
        err?.apiError?.message || err?.message || 'Failed to forage. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
      wanderingModal.hide();
    }
  };

  const selected = getSelectedTracks();
  const hasResults = results.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Forage</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Tell us what you're looking for and why — we'll trace the DNA of a sound across eras.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8 space-y-5">
          {/* Seeking */}
          <div>
            <label htmlFor="seeking" className="block text-sm font-semibold text-gray-900 mb-1">
              What are you looking for?
            </label>
            <input
              id="seeking"
              type="text"
              value={seeking}
              onChange={(e) => setSeeking(e.target.value)}
              placeholder="e.g. driving basslines, mechanical groove"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Context */}
          <div>
            <label htmlFor="context" className="block text-sm font-semibold text-gray-900 mb-1">
              What's your context?
            </label>
            <textarea
              id="context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. I just got a new eurorack module and want to understand the technique"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              required
            />
          </div>

          {/* Avoid */}
          <div>
            <label htmlFor="avoid" className="block text-sm font-semibold text-gray-900 mb-1">
              What shouldn't we return?{' '}
              <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              id="avoid"
              type="text"
              value={avoid}
              onChange={(e) => setAvoid(e.target.value)}
              placeholder="e.g. no mainstream pop, no obvious picks"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Depth + Submit */}
          <div className="flex items-center justify-between gap-4 pt-1">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Depth:</label>
              <div className="flex rounded-lg overflow-hidden border border-gray-200">
                {(['quick', 'balanced', 'deep'] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setSearchDepth(d)}
                    className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                      searchDepth === d
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !seeking.trim() || !context.trim()}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Foraging...' : 'Forage'}
            </button>
          </div>
        </form>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-12 h-12 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin"
                  style={{ animationDirection: 'reverse' }}
                />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Tracing the lineage...</h3>
            <p className="text-sm text-gray-500 text-center max-w-sm">
              Searching across eras for founding techniques, peak practitioners, and modern inheritors
            </p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-5">
            <h3 className="text-red-800 font-semibold mb-1 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              Error
            </h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Results */}
        {!loading && hasResults && (
          <div className="space-y-6">
            {/* Interpretation */}
            {interpretation && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-900 mb-1">
                      How we traced this sound:
                    </h3>
                    <p className="text-sm text-amber-800">{interpretation}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-amber-700 flex-shrink-0">
                    {cacheHit && (
                      <span className="px-2 py-1 bg-amber-100 rounded whitespace-nowrap">Cached</span>
                    )}
                    <span className="px-2 py-1 bg-amber-100 rounded whitespace-nowrap">
                      {(responseTimeMs / 1000).toFixed(1)}s
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Results header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {results.length} Tracks Found
              </h2>
              <button
                onClick={() => openExportModal(results)}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Export to Playlist
              </button>
            </div>

            {/* Masonry grid */}
            <Masonry
              breakpointCols={{ default: 2, 1024: 1 }}
              className="flex -ml-4 w-auto"
              columnClassName="pl-4 bg-clip-padding"
            >
              {results.map((track) => (
                <div key={track.id} className="mb-4">
                  <TrackCard
                    track={track}
                    selected={selectedTracks.has(track.id)}
                    onToggleSelect={toggleTrackSelection}
                    connectionType={track.metadata?.connection_type as string | undefined}
                  />
                </div>
              ))}
            </Masonry>
          </div>
        )}

        {/* Empty state — form filled but no results yet */}
        {!loading && !error && !hasResults && (seeking || context) && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <span className="text-5xl mb-4">🌿</span>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ready to forage</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Fill in what you're looking for and your context, then hit Forage.
            </p>
          </div>
        )}
      </div>

      {/* Fixed bottom bar — selected track actions */}
      {selected.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <p className="text-sm text-gray-700 font-medium">
              {selected.length} track{selected.length !== 1 ? 's' : ''} selected
            </p>
            <button
              onClick={() => openExportModal(selected)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Export Selected to Playlist
            </button>
          </div>
        </div>
      )}

      <ExportPlaylistModal />
      <WanderingModal allowDismiss onDismiss={() => { setLoading(false); wanderingModal.hide(); }} />
    </div>
  );
}
